import { css } from 'aphrodite';
import React, { useState } from 'react';
import { DragDropContext, Draggable, Droppable, DroppableProvided, DropResult } from 'react-beautiful-dnd';
import { Button } from '../../../../../../../components-v2/Button';
import gripIconUrl from '../../../../../../../components/boards/EditBoardColumnDefinition/kanbanBoardColumnDefinitionGrip.svg';
import { styleSheet } from './styles';

/**
 * A generic reorderable item — requires only an `id` field.
 * Callers extend this with their domain shape.
 */
export interface ReorderableItem {
	id: string;
}

/**
 * Props for the generic ReorderableList primitive.
 * Contains no survey-specific logic — purely a drag-to-reorder list
 * with "Update Order" / "Cancel" footer buttons.
 */
export interface ReorderableListProps<T extends ReorderableItem> {
	/** Modal / panel title shown at the top */
	title: string;
	/** Optional secondary description shown below the title */
	subtitle?: string;
	/** Shown under subtitle when the caller excluded some items (e.g. unsaved) */
	unsavedNotice?: string;
	/** Already-filtered list of reorderable items */
	items: T[];
	/** Unique ID for the react-beautiful-dnd droppable region */
	droppableId: string;
	/** Renders the middle-column content for a row */
	renderContent: (item: T, index: number) => React.ReactNode;
	/** Called with the new ordered IDs when the user clicks "Update Order" */
	onCommit: (orderedIds: string[]) => void;
	/** Called when the user clicks "Cancel" */
	onCancel: () => void;
}

/**
 * Generic drag-and-drop list primitive.
 * Holds item order in local state; splices source→destination on drag end.
 * Renders a grip handle, position number, and caller-supplied row content.
 */
export function ReorderableList<T extends ReorderableItem>({
	title,
	subtitle,
	unsavedNotice,
	items,
	droppableId,
	renderContent,
	onCommit,
	onCancel,
}: ReorderableListProps<T>): React.ReactElement {
	const [order, setOrder] = useState<T[]>(items);

	const onDragEnd = (result: DropResult) => {
		if (!result.destination || result.source.index === result.destination.index) {
			return;
		}
		const next = [...order];
		const [removed] = next.splice(result.source.index, 1);
		next.splice(result.destination.index, 0, removed);
		setOrder(next);
	};

	const handleCommit = () => {
		onCommit(order.map(item => item.id));
	};

	return (
		<div className={css(styleSheet.container)}>
			<div className={css(styleSheet.header)}>
				<div className={css(styleSheet.title)}>{title}</div>
				{subtitle && <div className={css(styleSheet.subtitle)}>{subtitle}</div>}
				{unsavedNotice && <div className={css(styleSheet.unsavedNotice)}>{unsavedNotice}</div>}
			</div>
			<DragDropContext onDragEnd={onDragEnd}>
				<div className={css(styleSheet.body)}>
					<Droppable direction='vertical' droppableId={droppableId}>
						{(droppableProvided: DroppableProvided) => (
							<div
								className={css(styleSheet.list)}
								ref={droppableProvided.innerRef}
								{...droppableProvided.droppableProps}
							>
								{order.map((item, index) => (
									<Draggable key={item.id} draggableId={item.id} index={index}>
										{(provided, snapshot) => (
											<div
												ref={provided.innerRef}
												{...provided.draggableProps}
												className={css(styleSheet.row, snapshot.isDragging ? styleSheet.rowDragging : undefined)}
											>
												<div className={css(styleSheet.dragHandle)}>
													<img {...provided.dragHandleProps} src={gripIconUrl} alt='drag handle' />
												</div>
												<div className={css(styleSheet.positionNumber)}>{index + 1}</div>
												<div className={css(styleSheet.content)}>{renderContent(item, index)}</div>
											</div>
										)}
									</Draggable>
								))}
								{droppableProvided.placeholder}
							</div>
						)}
					</Droppable>
				</div>
			</DragDropContext>
			<div className={css(styleSheet.footer)}>
				<Button variant='primary' onClick={handleCommit} type='button'>
					Update Order
				</Button>
				<Button variant='secondary' onClick={onCancel} type='button'>
					Cancel
				</Button>
			</div>
		</div>
	);
}
